import { animate, AnimatePresence, motion, useMotionValue } from 'motion/react';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Avatar } from 'ui/components/Avatar';
import { cn } from 'utils/cn';
import { useCollaborativePointers } from '../../hooks/use-realtime-pointers';
import { useAppSelector } from '../../store';
import { getBrowseMode, getDisplayMode } from '../../store/uiSlice';

interface PointerAreaBoxProps {
    className?: string;
}

export const PointerAreaBox = ({
    className
}: PointerAreaBoxProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [documentDimensions, setDocumentDimensions] = useState({ width: 0, height: 0 });
    const [isMouseInside, setIsMouseInside] = useState(false);
    const browseMode = useAppSelector(getBrowseMode);
    const displayMode = useAppSelector(getDisplayMode);

    const { pointers, handleMouseMove, handleMouseLeave, handleMouseEnter } = useCollaborativePointers({
        enabled: !browseMode && displayMode === 'desktop',
    });

    // Get full document dimensions
    const getDocumentDimensions = useCallback(() => {
        const docElement = document.documentElement;
        const body = document.body;

        const width = Math.max(
            docElement.scrollWidth,
            docElement.offsetWidth,
            docElement.clientWidth,
            body?.scrollWidth || 0,
            body?.offsetWidth || 0
        );

        const height = Math.max(
            docElement.scrollHeight,
            docElement.offsetHeight,
            docElement.clientHeight,
            body?.scrollHeight || 0,
            body?.offsetHeight || 0
        );

        return { width, height };
    }, []);

    // Update document dimensions when content changes
    useEffect(() => {
        const updateDimensions = () => {
            const dimensions = getDocumentDimensions();
            setDocumentDimensions(dimensions);
        };

        updateDimensions();

        // Watch for content changes that might affect document size
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(document.body);
        resizeObserver.observe(document.documentElement);

        // Also listen for window resize and scroll events
        window.addEventListener('resize', updateDimensions);
        window.addEventListener('scroll', updateDimensions);

        // Watch for DOM mutations that might change document size
        const mutationObserver = new MutationObserver(() => {
            // Debounce dimension updates
            setTimeout(updateDimensions, 100);
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
            window.removeEventListener('scroll', updateDimensions);
        };
    }, [getDocumentDimensions]);

    // Handle mouse events across the entire document
    const handleDocumentMouseMove = useCallback((e: MouseEvent) => {
        // Always consider mouse "inside" for document-level tracking
        if (!isMouseInside) {
            setIsMouseInside(true);
            handleMouseEnter?.();
        }

        if (handleMouseMove) {
            handleMouseMove(e);
        }
    }, [handleMouseMove, handleMouseEnter, isMouseInside]);

    const handleDocumentMouseLeave = useCallback((e: MouseEvent) => {
        // Only trigger leave when mouse actually leaves the viewport
        const leftViewport = (
            e.clientX <= 0 ||
            e.clientY <= 0 ||
            e.clientX >= window.innerWidth ||
            e.clientY >= window.innerHeight
        );

        if (leftViewport) {
            setIsMouseInside(false);
            handleMouseLeave?.();
        }
    }, [handleMouseLeave]);

    const handleVisibilityChange = useCallback(() => {
        if (document.hidden) {
            setIsMouseInside(false);
            handleMouseLeave?.();
        }
    }, [handleMouseLeave]);

    // Set up document-level event listeners
    useEffect(() => {
        document.addEventListener('mousemove', handleDocumentMouseMove, { passive: true });
        document.addEventListener('mouseleave', handleDocumentMouseLeave, { passive: true });
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('mousemove', handleDocumentMouseMove);
            document.removeEventListener('mouseleave', handleDocumentMouseLeave);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleDocumentMouseMove, handleDocumentMouseLeave, handleVisibilityChange]);

    return (
        <div
            ref={ref}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: `${documentDimensions.height}px`,
                pointerEvents: 'none',
                overflow: 'hidden',
            }}
            className={cn(className)}
        >
            <AnimatePresence mode="popLayout">
                {pointers.map((pointer) => (
                    <FollowPointer
                        key={pointer.userId}
                        x={pointer.x}
                        y={pointer.y}
                        title={pointer.title}
                        src={pointer.src}
                        userId={pointer.userId}
                        message={pointer.message}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};

interface FollowPointerProps {
    x: number;
    y: number;
    title: string;
    src: string | null;
    userId: string;
    message?: string;
}

export const FollowPointer = forwardRef<HTMLDivElement, FollowPointerProps>((props, ref) => {
    const { x, y, title, src, userId, message } = props;
    const motionX = useMotionValue(x);
    const motionY = useMotionValue(y);

    useEffect(() => {
        animate(motionX, x, {
            type: 'spring',
            stiffness: 100,
            damping: 20,
        });
        animate(motionY, y, {
            type: 'spring',
            stiffness: 100,
            damping: 20,
        });
    }, [x, y, motionX, motionY]);

    const colors = [
        '#6D5DF3',
        '#FF5A48',
        '#3ED696',
        '#279AF1',
        '#E72D67',
        '#FFA532',
        '#FCD227',
        '#272D3C',
    ];

    const getUserColor = (userId: string): string => {
        if (!userId) return colors[0];

        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
            const char = userId.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }

        return colors[Math.abs(hash) % colors.length];
    };

    const backgroundColor = getUserColor(userId);
    const hasMessage = message && message.trim() !== '';

    return (
        <motion.div
            ref={ref} // Now we can properly forward the ref
            className="absolute flex flex-col items-start justify-center"
            style={{
                left: motionX,
                top: motionY,
                pointerEvents: 'none',
                zIndex: 100,
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
                scale: 1,
                opacity: 1,
                transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 30
                }
            }}
            exit={{
                scale: 0,
                opacity: 0,
                transition: {
                    duration: 0.2
                }
            }}
        >
            {/* Rest of your component remains the same */}
            {hasMessage ? (
                <>
                    <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="1"
                        viewBox="0 0 28 28"
                        style={{
                            color: backgroundColor,
                            stroke: backgroundColor,
                            pointerEvents: 'none',
                        }}
                        className="h-5 w-5"
                        width="28"
                        height="28"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M0.06721 1.13922C0.00221718 0.989225 -0.0161776 0.823157 0.0144148 0.662577C0.0450073 0.501997 0.123174 0.354324 0.238763 0.238735C0.354352 0.123145 0.502025 0.0449791 0.662605 0.0143866C0.823185 -0.0162058 0.989254 0.00218895 1.13925 0.0671818L27.4873 10.7711C27.6475 10.8363 27.7831 10.9504 27.8748 11.0971C27.9665 11.2438 28.0097 11.4156 27.9982 11.5883C27.9867 11.7609 27.9212 11.9255 27.8108 12.0587C27.7005 12.192 27.551 12.2871 27.3835 12.3306L17.7931 15.592C17.2233 15.7385 16.7031 16.0349 16.2867 16.4505C15.8703 16.8661 15.5727 17.3856 15.4251 17.9551L12.3306 27.3835C12.2871 27.551 12.192 27.7005 12.0588 27.8108C11.9255 27.9211 11.7609 27.9867 11.5883 27.9982C11.4157 28.0097 11.2438 27.9665 11.0971 27.8748C10.9505 27.7831 10.8364 27.6475 10.7711 27.4873L0.06721 1.13922Z" />
                    </svg>
                    <motion.div
                        style={{
                            backgroundColor,
                            pointerEvents: 'none',
                        }}
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{
                            scale: 1,
                            opacity: 1
                        }}
                        exit={{ scale: 0.5, opacity: 0, transition: { duration: 1 } }}
                        className="inline-flex min-w-0 transform gap-1 whitespace-nowrap rounded-xl rounded-tl-[3px] p-1 text-xs text-white ml-4 -mt-1"
                    >
                        <Avatar
                            size="sm"
                            borderRadius="lg"
                            className="border-none text-gray-400 flex-shrink-0"
                            colour="#fff"
                            src={src ?? ''}
                            title={title}
                        />
                        <div className='flex flex-col gap-0.5'>
                            <span className="pr-1 truncate font-bold">{title || 'Unknown User'}</span>
                            <span className="truncate text-sm">{message.trim()}</span>
                        </div>
                    </motion.div>
                </>
            ) : (
                <>
                    <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="1"
                        viewBox="0 0 28 28"
                        style={{
                            color: backgroundColor,
                            stroke: backgroundColor,
                            pointerEvents: 'none',
                        }}
                        className="h-5 w-5"
                        width="28"
                        height="28"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M0.06721 1.13922C0.00221718 0.989225 -0.0161776 0.823157 0.0144148 0.662577C0.0450073 0.501997 0.123174 0.354324 0.238763 0.238735C0.354352 0.123145 0.502025 0.0449791 0.662605 0.0143866C0.823185 -0.0162058 0.989254 0.00218895 1.13925 0.0671818L27.4873 10.7711C27.6475 10.8363 27.7831 10.9504 27.8748 11.0971C27.9665 11.2438 28.0097 11.4156 27.9982 11.5883C27.9867 11.7609 27.9212 11.9255 27.8108 12.0587C27.7005 12.192 27.551 12.2871 27.3835 12.3306L17.7931 15.592C17.2233 15.7385 16.7031 16.0349 16.2867 16.4505C15.8703 16.8661 15.5727 17.3856 15.4251 17.9551L12.3306 27.3835C12.2871 27.551 12.192 27.7005 12.0588 27.8108C11.9255 27.9211 11.7609 27.9867 11.5883 27.9982C11.4157 28.0097 11.2438 27.9665 11.0971 27.8748C10.9505 27.7831 10.8364 27.6475 10.7711 27.4873L0.06721 1.13922Z" />
                    </svg>

                    <motion.div
                        style={{
                            backgroundColor,
                            pointerEvents: 'none',
                        }}
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{
                            scale: 1,
                            opacity: 1
                        }}
                        exit={{ scale: 0.5, opacity: 0 }}
                        className="inline-flex min-w-0 transform items-center gap-1 whitespace-nowrap rounded-xl rounded-tl-[3px] p-1 text-xs text-white ml-4 -mt-1"
                    >
                        <Avatar
                            size="sm"
                            borderRadius="lg"
                            className="border-none text-gray-400 flex-shrink-0"
                            colour="#fff"
                            src={src ?? ''}
                            title={title}
                        />
                        <span className="pr-1 truncate font-bold">{title || 'Unknown User'}</span>
                    </motion.div>
                </>
            )}
        </motion.div>
    );
});